import React, { Dispatch, SetStateAction, useState, useEffect, useRef } from "react";
import { GiHamburgerMenu } from "react-icons/gi";
import { useNavigate } from "react-router-dom";
import { GoChevronDown } from "react-icons/go";
import { useTranslation } from "react-i18next";
import LanguageSwitch from "../Languages/LanguageSwitch";
import { MenuContent } from "./MenuContent";
import { useOpenCloseMenu } from "../../hooks/useOpenCloseMenu";
import ButtonFullWidth from "../Buttons/ButtonFullWidth";
import ReactDOM from "react-dom";

function DropdownMenu(
  props: Readonly<{
    buttonName?: string;
    setIsAllMenuOpen: Dispatch<SetStateAction<boolean>>;
    isAllMenuOpen: boolean;
    isChildMenu: boolean;
    isInline?: boolean;
    menuContent?: MenuContent[];
  }>
) {
  const navigate = useNavigate();
  const [isMouseOut, setIsMouseOut] = useState(false);
  const [isCurrentMenuOpen, setIsCurrentMenuOpen] = useState(false);
  const { t } = useTranslation();

  // State per mantenere il menu montato durante la transizione di uscita
  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimeout = useRef<NodeJS.Timeout | null>(null);

  // UseOpenCloseMenu solo per menu non-hamburger (hover behavior)
  useOpenCloseMenu({ 
    isMouseOut: props.buttonName !== "hamburger" ? isMouseOut : false, 
    setIsCurrentMenuOpen: props.buttonName !== "hamburger" ? setIsCurrentMenuOpen : undefined
  });

  // Gestione del montaggio/smontaggio del menu hamburger
  useEffect(() => {
    if (props.buttonName === "hamburger") {
      if (props.isAllMenuOpen && isCurrentMenuOpen) {
        // Menu deve aprirsi
        setIsClosing(false);
        if (closeTimeout.current) {
          clearTimeout(closeTimeout.current);
          closeTimeout.current = null;
        }
        setShowHamburgerMenu(true);
      } else if (showHamburgerMenu && !isClosing) {
        // Menu deve chiudersi, inizia transizione
        setIsClosing(true);
        closeTimeout.current = setTimeout(() => {
          setShowHamburgerMenu(false);
          setIsClosing(false);
        }, 700); // durata della transizione CSS
      }
    }

    return () => {
      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
      }
    };
  }, [props.isAllMenuOpen, isCurrentMenuOpen, props.buttonName, showHamburgerMenu, isClosing]);

  // Helper per chiusura animata
  const closeMenuWithTransition = () => {
    props.setIsAllMenuOpen(false);
    setIsCurrentMenuOpen(false);
  };

  return (
    <div
      className=" w-full "
      onMouseEnter={() => {
        if (props.buttonName !== "hamburger") {
          setIsMouseOut(false);
          props.setIsAllMenuOpen(true);
          setIsCurrentMenuOpen(true);
        }
      }}
      onMouseLeave={() => {
        if (props.buttonName !== "hamburger") {
          setIsMouseOut(true);
        }
      }}
      role="none"
    >
      <button
        type="button"
        onClick={() => {
          if (!props.isChildMenu) {
            if (props.buttonName === "hamburger") {
              // Per hamburger, semplicemente toggli lo stato
              const newState = !props.isAllMenuOpen;
              props.setIsAllMenuOpen(newState);
              setIsCurrentMenuOpen(newState);
            } else {
              setIsMouseOut(false);
              props.setIsAllMenuOpen(!props.isAllMenuOpen);
              setIsCurrentMenuOpen(!props.isAllMenuOpen);
            }
          } else {
            setIsCurrentMenuOpen(!isCurrentMenuOpen);
          }
        }}
        className="w-full"
      >
        {props.buttonName === "hamburger" && (
          <GiHamburgerMenu className="text-4xl" />
        )}
        {props.buttonName !== "hamburger" && (
          <div
            className={
              props.isInline
                ? "flex items-center pl-[7px] pr-[7px] md:pt-[8px] md:pb-[8px] rounded-md hover:bg-lightbg-midGrey "
                : "w-full flex items-center  pl-[20px] pt-[4px] pb-[4px] md:pt-[8px] md:pb-[8px] rounded-md hover:bg-lightbg-midGrey "
            }
          >
            {t([`Menu.${props.buttonName}`])}
            <GoChevronDown />
          </div>
        )}
      </button>
      {/* Hamburger overlay menu */}
      {props.buttonName === "hamburger" && showHamburgerMenu && (
        typeof window !== "undefined" &&
        ReactDOM.createPortal(
          <div
            className={`
          fixed top-0 right-0 h-screen w-full sm:w-[320px] max-w-full z-[100000]
          bg-lightbg-headerPrimary shadow-2xl border-l border-lightbg-midGrey
          transition-transform duration-700 ease-in-out
          ${props.isAllMenuOpen && isCurrentMenuOpen && !isClosing 
            ? "translate-x-0 opacity-100 pointer-events-auto" 
            : "translate-x-full opacity-0 pointer-events-none"}
          will-change-transform
        `}
          >
            {/* Close button */}
            <button
              className="absolute top-3 right-4 text-4xl text-font-header hover:text-sky-700 transition-colors z-[100001] font-bold"
              aria-label="Chiudi menu"
              onClick={closeMenuWithTransition}
            >
              &times;
            </button>
            <div className="flex flex-col items-start pt-8 pb-8 px-4 sm:px-6 h-full overflow-y-auto">
              {props.menuContent?.map((menuContent) => {
                switch (menuContent.Type) {
                  case "Route": {
                    if (menuContent.Route?.IsMainRoute) {
                      return (
                        <ButtonFullWidth
                          key={"dropdown" + menuContent.Route?.Name}
                          onClick={() => {
                            closeMenuWithTransition();
                            setTimeout(() => {
                              navigate(menuContent.Route?.Route ?? "");
                            }, 700); // Wait for transition to finish
                          }}
                        >
                          {t("Menu." + menuContent.Route?.Name)}
                        </ButtonFullWidth>
                      );
                    } else {
                      return (
                        <DropdownMenu
                          key={"dropdown" + menuContent.Route?.Name}
                          buttonName={menuContent.Name}
                          setIsAllMenuOpen={props.setIsAllMenuOpen}
                          isAllMenuOpen={props.isAllMenuOpen}
                          isChildMenu={true}
                          isInline={false}
                          menuContent={menuContent.SubMenu}
                        />
                      );
                    }
                  }
                  case "Language": {
                    return (
                      <LanguageSwitch
                        key="languagedropdown"
                        setIsAllMenuOpen={props.setIsAllMenuOpen}
                        isAllMenuOpen={props.isAllMenuOpen}
                        languages={menuContent.Languages}
                        isInline={false}
                      />
                    );
                  }
                  case "Link": {
                    return <div key={menuContent.Name}></div>;
                  }
                  default: {
                    return <div key={menuContent.Name}></div>;
                  }
                }
              })}
            </div>
          </div>,
          document.body
        )
      )}
      {/* Regular dropdown for non-hamburger */}
      {props.buttonName !== "hamburger" && (
        <div className="">
          {props.isAllMenuOpen && isCurrentMenuOpen && (
            <div
              className={
                "flex flex-col items-start rounded-md pt-[5px] pb-[5px] w-[100px] md:w-[208px] pl-[5px] pr-[5px] absolute right-[90%] shadow-2xl border-lightbg-midGrey border-solid border-[1px] bg-lightbg-headerPrimary"
              }
            >
              {props.menuContent?.map((menuContent) => {
                switch (menuContent.Type) {
                  case "Route": {
                    if (menuContent.Route?.IsMainRoute) {
                      return (
                        <ButtonFullWidth
                          key={"dropdown" + menuContent.Route?.Name}
                          onClick={() => {
                            props.setIsAllMenuOpen(false);
                            setIsCurrentMenuOpen(false);
                            navigate(menuContent.Route?.Route ?? "");
                          }}
                        >
                          {t("Menu." + menuContent.Route?.Name)}
                        </ButtonFullWidth>
                      );
                    } else {
                      return (
                        <DropdownMenu
                          key={"dropdown" + menuContent.Route?.Name}
                          buttonName={menuContent.Name}
                          setIsAllMenuOpen={props.setIsAllMenuOpen}
                          isAllMenuOpen={props.isAllMenuOpen}
                          isChildMenu={true}
                          isInline={false}
                          menuContent={menuContent.SubMenu}
                        />
                      );
                    }
                  }
                  case "Language": {
                    return (
                      <LanguageSwitch
                        key="languagedropdown"
                        setIsAllMenuOpen={props.setIsAllMenuOpen}
                        isAllMenuOpen={props.isAllMenuOpen}
                        languages={menuContent.Languages}
                        isInline={false}
                      />
                    );
                  }
                  case "Link": {
                    return <div></div>;
                  }
                  default: {
                    return <div></div>;
                  }
                }
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DropdownMenu;
